import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useTrialDetails from '../../../../../containers/team/hooks/useTrialDetails';
import useAnalyticsTracker from '../../../../../analytics/hooks/useAnalyticsTracker';
import useSettingsStorageService from '../../../../../auth/hooks/useSettingsStorageService';
import { PipelineListTrackers } from '../../../tracking/pipeline-list-trackers';

const SCHEDULE_DEMO_POPUP_SHOWN_KEY = 'schedule_demo_popup_shown';
export const SCHEDULE_DEMO_FIRST_SEEN_KEY = 'schedule_demo_first_seen';

// Gives a new user time to explore before the exit-intent prompt can fire.
export const SCHEDULE_DEMO_GRACE_PERIOD = 5 * 60 * 1000;

const ALLOWED_PATH_PATTERNS = [
  /^\/$/,
  /^\/pipeline$/,
  /^\/pipeline\/create(\/|$)/,
  /^\/pipeline\/edge\/\d+\/create(\/|$)/
];

function isAllowedPath(pathname: string): boolean {
  return ALLOWED_PATH_PATTERNS.some(pattern => pattern.test(pathname));
}

function isAlreadyShown(): boolean {
  try {
    return !!localStorage.getItem(SCHEDULE_DEMO_POPUP_SHOWN_KEY);
  } catch {
    return true;
  }
}

type SettingsStorage = ReturnType<typeof useSettingsStorageService>;

// Kept per user, so a second account on the same browser starts its own grace period.
function getFirstSeen({ getSettings, applySettings }: SettingsStorage): number {
  try {
    const stored = Number(getSettings(SCHEDULE_DEMO_FIRST_SEEN_KEY));

    // A future timestamp (clock skew, tampering) would postpone the prompt indefinitely.
    if (stored > 0 && stored <= Date.now()) {
      return stored;
    }

    const now = Date.now();
    applySettings(SCHEDULE_DEMO_FIRST_SEEN_KEY, now);
    return now;
  } catch {
    return Date.now();
  }
}

function markAsShown(): void {
  try {
    localStorage.setItem(SCHEDULE_DEMO_POPUP_SHOWN_KEY, 'true');
  } catch {
    // localStorage unavailable
  }
}

export function useExitIntent() {
  const [showModal, setShowModal] = useState(false);
  const listenerAttached = useRef(false);
  const triggeredRef = useRef(false);
  const { isTrialUser } = useTrialDetails();
  const { eventTrack } = useAnalyticsTracker();
  const location = useLocation();
  const settingsStorage = useSettingsStorageService();

  const isOnAllowedPage = isAllowedPath(location.pathname);

  const triggerPopup = useCallback(() => {
    if (triggeredRef.current || !isAllowedPath(window.location.pathname) || isAlreadyShown()) {
      return;
    }
    triggeredRef.current = true;
    setShowModal(true);
    eventTrack({
      action: PipelineListTrackers.PIPELINE_TRACKER_SCHEDULE_DEMO_POPUP_SHOWN
    });
  }, []);

  useEffect(() => {
    if (!isTrialUser || !isOnAllowedPage || listenerAttached.current) {
      return;
    }

    if (isAlreadyShown()) {
      return;
    }

    listenerAttached.current = true;
    const firstSeen = getFirstSeen(settingsStorage);

    const handleMouseLeave = () => {
      if (Date.now() - firstSeen < SCHEDULE_DEMO_GRACE_PERIOD) {
        return;
      }

      triggerPopup();
      document.documentElement.removeEventListener('mouseleave', handleMouseLeave);
    };

    document.documentElement.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      listenerAttached.current = false;
      document.documentElement.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isTrialUser, isOnAllowedPage]);

  const dismissModal = useCallback(() => {
    setShowModal(false);
    markAsShown();
    eventTrack({
      action: PipelineListTrackers.PIPELINE_TRACKER_SCHEDULE_DEMO_POPUP_DISMISSED
    });
  }, []);

  return { showModal, dismissModal };
}
